export type PortalGuardDecision = 'loading' | 'sign-in' | 'offline' | 'error' | 'ready';

export interface PortalGuardInputs {
  hasSession: boolean;
  sessionLoading: boolean;
  portalSessionLoading: boolean;
  /** `usePortalSession().isError` — fix round 1, item 4: the business root
   * guard previously ignored this entirely and fell through to rendering
   * the full shell with an empty membership list on a failed
   * `portal-session` call, which (combined with item 4's other bug) showed
   * every owner-only nav item to whoever was signed in. */
  portalSessionError: boolean;
  /** `usePortalSession().fetchStatus` — fix round 2 (minor), mirrors
   * `adminGuard.ts`'s same field: a fetch PAUSED by TanStack Query's
   * default `networkMode: 'online'` while offline is a waiting state, not
   * a server error. */
  portalSessionFetchStatus: 'fetching' | 'paused' | 'idle';
  /** Whether `portal-session` has ever returned data for this session —
   * distinct from `!portalSessionLoading`, which a failed query also
   * satisfies. */
  portalSessionLoaded: boolean;
}

/**
 * The business root guard's decision — pure, mirroring `adminGuard.ts`'s
 * `decideAdminGuard` so both guards are Vitest-testable without rendering
 * the shell or a running Supabase instance. `'ready'` covers both "has
 * memberships" and "signed in with none yet" — the nav/property-switcher
 * components downstream already handle a `role === null` /
 * `memberships.length === 0` state correctly (minimal nav, no switcher).
 */
export function decidePortalGuard({
  hasSession,
  sessionLoading,
  portalSessionLoading,
  portalSessionError,
  portalSessionFetchStatus,
  portalSessionLoaded,
}: PortalGuardInputs): PortalGuardDecision {
  if (sessionLoading) return 'loading';
  if (!hasSession) return 'sign-in';
  // See adminGuard.ts's matching comment: a paused fetch with no cached
  // answer yet is waiting on the network, not broken or stuck loading.
  if (portalSessionFetchStatus === 'paused' && !portalSessionLoaded) return 'offline';
  if (portalSessionLoading) return 'loading';
  // A failed REFETCH with an answer already cached (restored from the
  // desk's persisted cache, or fetched earlier in the session) keeps serving
  // it: the property list doesn't change because the network blipped, and
  // an offline reload of the check-in desk must not dead-end on "Couldn't
  // load your account" (Task 14 fix round 1).
  if (portalSessionError && !portalSessionLoaded) return 'error';
  // Finished without ever producing data is an error too.
  return portalSessionLoaded ? 'ready' : 'error';
}
